import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { InvoiceDetailsPageContext } from "@insite/content-library/Pages/InvoiceDetailsPage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIInvoiceDetailsPrintView from "@NA/Components/ADIInvoiceDetailsPrintView";
import adiLoadInvoiceDetails from "@NA/Handlers/Invoices/ADILoadInvoiceDetails";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";

export interface ADIInvoiceDetailsStyles {
    titleRow?: InjectableCss;
    title?: TypographyProps;
    errorTitle?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const adiInvoiceDetailsStyles: ADIInvoiceDetailsStyles = {
    titleRow: {
        css: css`
            width: 100%;
            padding: 0;
            @media print {
                display: none;
            }
        `,
    },
    title: {
        weight: "normal",
        size: "20px",
        as: "h1",
        color: `${ADIColors.black}`,
        css: css`
            width: 100%;
            padding: 0;
            margin: 0;
        `,
    },
    errorTitle: {
        size: "20px",
        as: "h1",
        color: `${ADIColors.red2}`,
        css: css`
            font-weight: 600;
            width: 100%;
            padding: 0;
            margin: 0;
            margin-top: 10px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 150px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = adiInvoiceDetailsStyles;

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    let invoiceNumber;
    let printOpen;
    let invoiceType;
    if (location && location.search) {
        const parsedQuery = parseQueryString<{ invoiceNumber: string; print: string; InvType: string }>(
            location.search,
        );
        invoiceNumber = parsedQuery.invoiceNumber;
        printOpen = parsedQuery.print;
        invoiceType = parsedQuery.InvType;
    }
    return {
        invoiceNumber,
        location,
        printOpen,
        invoiceDetailsData: state.ADIGlobalData.adiInvoiceDetails.InvoiceDetails,
        isLoading: state.ADIGlobalData.adiInvoiceDetails.isLoading,
        invoiceType,
    };
};

const mapDispatchToProps = {
    adiLoadInvoiceDetails,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ADIInvoiceDetails: React.FC<Props> = ({
    adiLoadInvoiceDetails,
    location,
    invoiceDetailsData,
    isLoading,
    invoiceNumber,
    printOpen,
    invoiceType,
}) => {
    React.useEffect(() => {
        adiLoadInvoiceDetails({
            invoiceNumber: invoiceNumber ?? "",
            invoiceType: invoiceType!.toString(),
        });
    }, []);
    const hasPrintedRef = React.useRef(false);

    React.useEffect(() => {
        if (printOpen === "1" && invoiceDetailsData?.invoices?.length && !hasPrintedRef.current) {
            hasPrintedRef.current = true;
            setTimeout(() => {
                openPrintDialog();
            }, 750);
        }
    }, [invoiceDetailsData?.invoices, printOpen]);

    if (!invoiceNumber) {
        return null;
    }

    return (
        <>
            {invoiceDetailsData && invoiceDetailsData?.invoices && invoiceDetailsData?.invoices.length > 0 ? (
                <ADIInvoiceDetailsPrintView
                    invoiceDetailsData={invoiceDetailsData}
                    isLoading={isLoading}
                ></ADIInvoiceDetailsPrintView>
            ) : (
                <StyledWrapper {...styles.titleRow}>
                    <Typography variant="h1" {...styles.title}>
                        {translate("Invoice Details")}
                    </Typography>
                    <>
                        {!isLoading ? (
                            <Typography variant="h1" {...styles.errorTitle}>
                                {translate("Invalid Request")}
                            </Typography>
                        ) : (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
                            </StyledWrapper>
                        )}
                    </>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ADIInvoiceDetails),
    definition: {
        group: "Invoice History",
        displayName: "ADI Invoice Details",
        allowedContexts: [InvoiceDetailsPageContext],
    },
};

export default widgetModule;
